import React, { useState, useMemo } from 'react';
import { ShieldCheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ManagedPropertyList from './ManagedPropertyList/ManagedPropertyList';
import { ManagedProperty } from '../property/property-card/PropertyCardTypes';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

interface ManagedPropertiesTabsProps {
  selectedTab: 'active' | 'archived';
  onTabChange: (tab: 'active' | 'archived') => void;
  activeCount: number;
  archivedCount: number;
  loading: boolean;
  error: string | null;
  displayedProperties: ManagedProperty[];
  unarchivingPropertyId: number | null;
  openAddTenantModal: (property: ManagedProperty) => void;
  openEditModal: (property: ManagedProperty) => void;
  openArchiveModal: (property: ManagedProperty) => void;
  submitUnarchiveProperty: (property: ManagedProperty) => void;
}

const ManagedPropertiesTabs: React.FC<ManagedPropertiesTabsProps> = ({
  selectedTab,
  onTabChange,
  activeCount,
  archivedCount,
  loading,
  error,
  displayedProperties,
  unarchivingPropertyId,
  openAddTenantModal,
  openEditModal,
  openArchiveModal,
  submitUnarchiveProperty,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const analytics = useAnalyticsWithConsent();

  // Filter properties based on search query
  const filteredProperties = useMemo(() => {
    if (!searchQuery.trim()) return displayedProperties;

    const query = searchQuery.toLowerCase();
    return displayedProperties.filter((property) => {
      const address = property.address?.toLowerCase() || '';
      const type = property.type?.toLowerCase() || '';
      const notes = property.notes?.toLowerCase() || '';
      const id = property.id?.toString() || '';

      return (
        address.includes(query) ||
        type.includes(query) ||
        notes.includes(query) ||
        id.includes(query)
      );
    });
  }, [displayedProperties, searchQuery]);

  return (
    <>
      {/* Search Bar */}
      <div className="mt-6">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const newQuery = e.target.value;
              setSearchQuery(newQuery);
              if (newQuery.trim()) {
                analytics.trackInteraction('managed_properties_search', {
                  search_query: newQuery,
                  results_count: filteredProperties.length,
                  active_tab: selectedTab,
                });
              }
            }}
            className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
            placeholder="Search by address, type, ID, or notes..."
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-3 border-b border-gray-200">
        {(['active', 'archived'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              onTabChange(tab);
              analytics.trackInteraction('managed_properties_tab_switched', {
                tab_name: tab,
                tab_count: tab === 'active' ? activeCount : archivedCount,
              });
            }}
            className={`py-3 text-sm font-semibold capitalize transition ${
              selectedTab === tab
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'active' ? `Active (${activeCount})` : `Archived (${archivedCount})`}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="mt-6 rounded-3xl bg-white p-6 shadow-xl">
        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-1/3 rounded bg-gray-200" />
            <div className="h-32 rounded-2xl bg-gray-100" />
            <div className="h-32 rounded-2xl bg-gray-100" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
            {error}
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 py-12 text-center">
            <ShieldCheckIcon className="h-12 w-12 text-gray-400" />
            <div>
              <p className="text-lg font-semibold text-gray-900">
                {searchQuery.trim()
                  ? 'No properties match your search'
                  : selectedTab === 'active'
                    ? 'No managed properties yet'
                    : 'No archived properties'}
              </p>
              <p className="text-gray-500">
                {searchQuery.trim()
                  ? 'Try adjusting your search terms'
                  : selectedTab === 'active'
                    ? 'Add properties to start tracking rent privately.'
                    : 'Archive a managed property to see it here.'}
              </p>
            </div>
          </div>
        ) : (
          <ManagedPropertyList
            properties={filteredProperties}
            unarchivingPropertyId={unarchivingPropertyId}
            openAddTenantModal={openAddTenantModal}
            openEditModal={openEditModal}
            openArchiveModal={openArchiveModal}
            submitUnarchiveProperty={submitUnarchiveProperty}
          />
        )}
      </div>
    </>
  );
};

export default ManagedPropertiesTabs;
